import React, { useContext, useMemo, useEffect, useRef, useState } from "react";
import Header3 from "../components/Header3";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Footer2 from "../components/Footer2";
import { useAuth } from "../context/auth";
import toast from "react-hot-toast";
import axios from "../AxiosInstance";
import { useParams } from "react-router-dom";
import { BASE_URL } from "../config/BaseUrls";
import { LanguageContext } from "../context/LanguageContext";
import { ChatState } from "../context/ChatProvider";
import { Frontend } from "../config/BaseUrls";
import { X as CloseIcon, MoreVertical, Share2 } from "lucide-react";
import { resolveBookImageUrl } from "../utils/urlHelpers";
import {
  ChevronDown,
  Search,
  ExternalLink,
  Star,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  LanguagesIcon,
  ChevronRight,
  Link as LinkIcon,
  Video,
  Instagram,
  Music,
  MessageCircle,
  Send,
  Camera,
} from "lucide-react";
// import descriptions from "../../back_End/bookDescriptions.json";
const brand = {
  teal: "#34c3c9",
  gray: "#a6a6a6",
  soft: "#f2f2f2",
};
const formatDate = (dateString) => {
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return new Date(dateString).toLocaleDateString("en-GB", options);
};
const Container = ({ children }) => (
  <div className="mx-auto w-full max-w-6xl px-4 sm:px-[24px] lg:px-[32px]">
    {children}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-3xl bg-white p-[20px] shadow-sm ring-1 ring-[#eff0f0] ${className}`}
  >
    {children}
  </div>
);

const Button = ({
  variant = "primary",
  className = "",
  children,
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-[20px] py-[8px] text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-[#34c3c9] text-white hover:brightness-95 focus:ring-[#34c3c9]",
    secondary:
      "bg-white text-[#34c3c9] ring-1 ring-[#34c3c9] hover:bg-[#f2f2f2] focus:ring-[#34c3c9]",
    ghost: "text-[#34c3c9] hover:bg-[#f2f2f2] focus:ring-[#34c3c9]",
    light:
      "bg-[#f7f7f7] text-slate-700 ring-1 ring-[#e9e9e9] hover:bg-[#f2f2f2]",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

/*************************
 * Stars (with halves)
 *************************/
const Stars = ({
  rating = 0,
  size = 18,
  className = "",
  onRate, // callback when a star is clicked
  disabled = false, // like hasSubmittedReview
}) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;

  return (
    <div
      className={`flex items-center gap-0.5 ${className}`}
      aria-label={`${rating} out of 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => {
        const filled = i < full || (i === full && half);
        const halfFill = i === full && half;

        return (
          <div
            key={i}
            className={`relative ${!disabled && "cursor-pointer"}`}
            style={{ width: size, height: size }}
            onClick={(e) => {
              if (!disabled && onRate) {
                onRate(i + 1, e); // stars are 1-indexed
              }
            }}
          >
            {/* Empty star background */}
            <Star
              className="absolute inset-0 text-gray-300"
              style={{ width: size, height: size }}
            />
            {/* Filled or half star overlay */}
            {filled && (
              <Star
                className="absolute inset-0 text-yellow-400"
                style={{
                  width: size,
                  height: size,
                  clipPath: halfFill ? "inset(0 50% 0 0)" : undefined,
                }}
                fill="#fbbf24"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/*************************
 * BUY ON (same pattern as Book Club/Duo)
 *************************/
const BuyOn = ({ catalog }) => {
  const { lg } = useContext(LanguageContext);
  const [open, setOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const hasManuallySelectedCountry = useRef(false);
  const allCountries = Object.keys(catalog);

  // ✅ Detect user's country automatically (only once, and only if user hasn't manually selected)
  useEffect(() => {
    // Don't auto-detect if user has manually selected a country
    if (hasManuallySelectedCountry.current) {
      return;
    }

    // Only auto-detect if catalog is loaded and activeCountry is not set
    if (Object.keys(catalog || {}).length === 0 || activeCountry !== null) {
      return;
    }

    const detectCountry = async () => {
      try {
        const res = await fetch("https://ipwho.is/");
        const data = await res.json();
        console.log(data, "ip dres");

        if (data && data.country) {
          const country = data.country;
          // Only auto-select if it's part of the catalog and user hasn't manually selected
          if (Object.keys(catalog || {}).includes(country) && !hasManuallySelectedCountry.current) {
            setActiveCountry(country);
          }
        }
      } catch (err) {
        console.error("Failed to detect country:", err);
      }
    };

    detectCountry();
  }, [catalog, activeCountry]);
  useEffect(() => {
    function onDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    window.addEventListener("keydown", onDown);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onDown);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const countries = useMemo(() => Object.keys(catalog || {}), [catalog]);
  const filtered = countries.filter((c) =>
    c.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative z-[50]" ref={ref}>
      <label className="mb-2 block text-sm font-medium text-slate-700">
        Buy on
      </label>
      <Button
        variant="light"
        className="w-full justify-between "
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          {activeCountry ? `${lg?.country || "Country"}: ${activeCountry}` : (lg?.chooseYourCountry || "Choose your country")}
        </span>
        <ChevronDown size={16} />
      </Button>

      {open && (
        <>
          {/* White backplate to cover underlying content (no dimming) */}
          <div className="relative md:absolute z-[60] md:left-full md:-top-24 ml-3 md:w-[560px] rounded-2xl border border-[#e9e9e9] bg-white shadow-xl" />

          {/* Flyout panel: countries (L) → sellers (R) */}
          <div className="relative md:absolute z-[60] md:left-full md:-top-24 ml-3 md:w-[560px] rounded-2xl border border-[#e9e9e9] bg-white shadow-xl">
            <div className="border-b border-[#f1f1f1] p-[12px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#a6a6a6]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search country..."
                  className="w-full rounded-xl border border-[#e9e9e9] bg-white py-2 pl-[36px] pr-[12px] text-sm focus:border-[#34c3c9] focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2">
              {/* Countries */}
              <div>
                {filtered.length === 0 ? (
                  <div className="px-[16px] py-[8px] text-center text-sm text-slate-500">
                    No countries
                  </div>
                ) : (
                  filtered.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        hasManuallySelectedCountry.current = true;
                        setActiveCountry(c);
                      }}
                      className={`flex w-full items-center justify-between px-[16px] py-[8px] text-left text-sm hover:bg-[#f9f9f9] transition-colors ${activeCountry === c ? "bg-[#f6fffe] text-[#0f766e] font-medium" : ""
                        }`}
                    >
                      <span>{c}</span>
                      <ChevronDown size={14} className="-rotate-90" />
                    </button>
                  ))
                )}
              </div>

              {/* Sellers */}
              <div className="sm:border-l border-t sm:border-t-0 border-[#f1f1f1] max-h-80 overflow-auto">
                {!activeCountry ? (
                  <div className="px-[16px] py-[24px] text-center text-sm text-slate-500">
                    <p className="mb-1">Select a country to see sellers</p>
                    <p className="text-xs text-slate-400">Choose from the list on the left</p>
                  </div>
                ) : !catalog[activeCountry] || catalog[activeCountry].length === 0 ? (
                  <div className="px-[16px] py-[24px] text-center text-sm text-slate-500">
                    <p className="mb-1">No sellers available</p>
                    <p className="text-xs text-slate-400">for {activeCountry}</p>
                  </div>
                ) : (
                  <div>
                    <div className="sticky top-0 bg-white border-b border-[#f1f1f1] px-[16px] py-[8px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {activeCountry} ({(catalog[activeCountry] || []).length} {(catalog[activeCountry] || []).length === 1 ? 'seller' : 'sellers'})
                    </div>
                    <div className="divide-y divide-[#f1f1f1]">
                      {(catalog[activeCountry] || []).map((s, index) => (
                      <a
                          key={s.name || index}
                          href={s.url || s.link || '#'}
                        target="_blank"
                        rel="noreferrer"
                          className="flex items-center justify-between px-[16px] py-[12px] text-sm hover:bg-[#f6fffe] transition-colors group"
                        onClick={() => setOpen(false)}
                      >
                          <span className="font-medium text-slate-700 group-hover:text-[#34c3c9] transition-colors">{s.name}</span>
                          <ExternalLink className="h-4 w-4 text-[#a6a6a6] group-hover:text-[#34c3c9] transition-colors" />
                      </a>
                    ))}
                      </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
/*************************
 * Review row with kebab menu
 *************************/
const ReviewRow = ({ review, onShare, onEdit, onDelete, show }) => {
  const [open, setOpen] = useState(false);
  const navigate=useNavigate()
  const ref = useRef(null);
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  return (
    <div className="py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <img
            onClick={() => {
              navigate(`/ambassadoruserprofile?id=${review?.userId?._id}`)
            }}
            src={
              review.userId?.profileImage
                ? `${BASE_URL}/uploads/${review.userId.profileImage}`
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  review?.userId?.name
                )}&background=34c3c9&color=fff`
            }
            alt="avatar"
            className="h-10 w-10 rounded-full cursor-pointer"
          />
          <div>
            <div className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
              <span>{review?.userId?.name}</span>
              <span className="font-normal text-slate-500">
                on {formatDate(review?.createdAt)}
              </span>
            </div>
            <Stars rating={review?.rating} size={16} className="mt-1" />
          </div>
        </div>

        {/* kebab */}
        <div className="relative" ref={ref}>
          {show && (
            <button
              className="p-1 text-slate-500 hover:text-slate-700"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          )}
          {open && (
            <div
              role="menu"
              className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-[#e9e9e9] bg-white p-1 shadow-xl"
            >
              <button
                role="menuitem"
                className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#f8f8f8]"
                onClick={() => {
                  setOpen(false);
                  onShare?.(review);
                }}
              >
                Share
              </button>
              <button
                role="menuitem"
                className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#f8f8f8]"
                onClick={() => {
                  setOpen(false);
                  onEdit?.(review);
                }}
              >
                Edit
              </button>
              <button
                role="menuitem"
                className="w-full rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                onClick={() => {
                  setOpen(false);
                  onDelete?.(review);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
      {review.title && (
        <div className="mt-2 text-base font-semibold text-slate-900">
          {review.title}
        </div>
      )}
      <p className="mt-1 text-[15px] leading-6 text-slate-800">{review.body}</p>
    </div>
  );
};

/*************************
 * Want to read Modal
 *************************/
const ChoiceModal = ({ open, active, onClose, onPick }) => {
  const { lg } = useContext(LanguageContext);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl rounded-[28px] border-2 border-[#34c3c9] bg-white p-8 shadow-2xl">
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute right-5 top-5 text-slate-500 hover:text-slate-700"
        >
          <CloseIcon />
        </button>
        <h3 className="mb-6 text-center text-2xl font-bold text-slate-900">
          Make a choice for this book
        </h3>
        <div className="mx-auto flex max-w-sm flex-col gap-4">
          <Button
            className="w-full px-4 py-2 text-sm"
            variant={active == "want" ? "primary" : "secondary"}
            onClick={() => onPick?.("want")}
          >
            {lg?.rewbtn1 || "Want to read"}
          </Button>
          <Button
            className="w-full px-4 py-2 text-sm"
            variant={active == "read" ? "primary" : "secondary"}
            onClick={() => onPick?.("read")}
          >
            {lg?.Read || "Read"}
          </Button>
        </div>
      </div>
    </div>
  );
};



const Reviews = () => {
  const { lg } = useContext(LanguageContext);
  var storedBook = null;
  const [isSearchBookSet, setIsSearchBookSet] = useState();
  const [showCan, setShowCan] = useState(false);
  const handleCloseCan = () => setShowCan(false);
  const handleShowCan = () => setShowCan(true);

  const [topOffset, setTopOffset] = useState(17); // Default top position
  const footerRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  // const [coverImage, setCoverImage] = useState(null);
  const [bookCoverImage, setBookCoverImage] = useState(null);
  const sanitizedCoverImage = useMemo(
    () =>
      resolveBookImageUrl(bookCoverImage),
    [bookCoverImage]
  );
  const [author, setAuthor] = useState("");
  const [titless, setTitless] = useState("");
  const [descriptions, setDescriptions] = useState("");
  const [sShow, setSShow] = useState(false); // For signup modal
  const handleCloseS = () => {
    setSShow(false); // Close signup modal
  };
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const footerHeight = entry.boundingClientRect.height;
          const footerVisible = Math.min(
            window.innerHeight - entry.boundingClientRect.top,
            footerHeight
          );

          // Calculate new top value based on how much the footer is visible
          const newTop = Math.max(
            17 - (footerVisible / footerHeight) * 38,
            -14
          );

          setTopOffset(newTop);
        } else {
          setTopOffset(17); // Reset to default when footer is not visible
        }
      },
      { threshold: Array.from({ length: 11 }, (_, i) => i * 0.1) } // More gradual steps
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);
  const [rating, setRating] = useState(0); // State to track the selected rating
  const [ratingUser, setRatingUser] = useState(0); // State to track the selected rating
  const [reviewIdD, setReviewIdD] = useState(null); // State to track the review ID for editing
  // Load the rating from localStorage when the component mounts
  const [buyCatalog, setBuyCatalog] = useState({}); // { "United Kingdom": [...], ... }

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await axios.get("api/v1/stores/");

        // Transform into { country: [stores] } shape
        const catalog = {};
        data.forEach((entry) => {
          catalog[entry.country.trim()] = entry.stores.map((store) => ({
            name: store.name,
            url: store.link,
          }));
        });

        console.log("Transformed buyCatalog:", catalog);
        setBuyCatalog(catalog);

        const countryList = data.map((entry) => entry.country.trim());
        console.log("Countries list:", countryList);
        setCountries(countryList);
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    };

    fetchStores();
  }, []);
  const handleRating = (event, index) => {
    setRating(index);
    // Save the rating to localStorage
    handleSubmitReview(event, "rating", index);
  };

  const handleRatingUser = (index) => {
    setRatingUser(index);
    // Save the rating to localStorage
    localStorage.setItem("userRatingRe", index);
  };

  const reviewFormRef = useRef(null);

  const handleScrollToForm = () => {
    reviewFormRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const navigate = useNavigate();

  // Went Reat MOdel
  const [wShow, setWShow] = useState(false); // For signup modal
  const handleCloseW = () => {
    setWShow(false);
  };

  const handleShowW = () => {
    setWShow(true); // Open  modal
  };

  // Alert Massage

  const [alertVisible, setAlertVisible] = useState(false);

  const handleSubmitRe = (event) => {
    event.preventDefault(); // Prevent form submission default behavior
    setAlertVisible(true); // Show the alert
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Adds smooth scrolling effect
    });

    setTimeout(() => setAlertVisible(false), 6000); // Hide the alert after 3 seconds
  };

  const handleCloseAlert = () => {
    setAlertVisible(false); // Manually close the alert
  };

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [selectedAction, setSelectedAction] = useState(null);

  const [showAll, setShowAll] = useState(false);

  const displayedReviews = showAll ? reviews : reviews.slice(0, 3); // Show first 6 books or all if showAll is true

  const handleSeeMoreClick = () => {
    setShowAll(true); // Show all books
  };

  const totalBooks = 8; // Update with your actual total book count
  const [bookId, setBookId] = useState(null);
  const [buttonText, setButtonText] = useState(lg?.rewbtn1); // Default Text
  useEffect(() => {
    const savedSelection = localStorage.getItem("bookSelection");
    if (savedSelection) {
      setButtonText(savedSelection); // Refresh hone ke baad bhi selected value set ho jaye
    } else {
      setButtonText(lg.rewbtn1);
    }
  }, [lg.rewbtn1]);

  const handleSelection = async (option) => {
    setButtonText(option);
    handleCloseW();
    if (!user || !user._id) {
      toast.error("You must be logged in to access this feature");
      setShowLoginM(true);
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const offilioFlag = searchParams.get("offilio");
    // const searchBookFlag = searchParams.get("searchbook");
    const searchBookFlag = searchParams.get("searchbook") === "true";

    try {
      await axios.post("api/v1/review/book-status", {
        userId: user._id, // context se lein
        bookId, // page ya props se lein
        ean: ean,
        status: option,
        offilio: offilioFlag,
        searchbook: searchBookFlag,
      });
      console.log("success");
      console.log("success");
    } catch (error) {
      console.error("Failed to save selection", error);
    }
  };

  // IMAGE GET FROM OTHER PAGE FOR REVIEW
  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    const savedBook = JSON.parse(
      localStorage.getItem("selectedBookforreviews")
    );
    if (savedBook) {
      setSelectedBook(savedBook);
      setBookId(savedBook._id); // Extract the bookId from the saved book object
    }
  }, []);
  const [rec, setRec] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const eanFromURL = searchParams.get("ean");
    const bookIdFromURL = searchParams.get("bookid");
    const searchbookFlag = searchParams.get("searchbook") === "true";
    const bookDetailFlag = searchParams.get("bookdetail") === "true";
    const offilioFlag = searchParams.get("offilio");

    if (eanFromURL) {
      setEan(eanFromURL);
      setBookId(eanFromURL);
      fetchReviews(eanFromURL);
    } else if (bookIdFromURL) {
      setBookId(bookIdFromURL);
      fetchReviews(bookIdFromURL);
    } else {
      // Fallback to localStorage
      const savedBook = JSON.parse(
        localStorage.getItem("selectedBookforreviews")
      );
      if (savedBook?.bookId) {
        setBookId(savedBook.bookId);
        fetchReviews(savedBook.bookId);
      } else if (savedBook?._id) {
        setBookId(savedBook._id);
        fetchReviews(savedBook._id);
      } else if (savedBook?.book?._id) {
        // Additional fallback: check nested book object
        setBookId(savedBook.book._id);
        fetchReviews(savedBook.book._id);
      }
    }
  }, [location.search, rec]);

  // Integration Reviews
  const [auth, setAuth] = useAuth(); // If using Context API
  const { user, setShowLoginM } = ChatState();
  const [title, setTitleBook] = useState("");
  const [description, setDescription] = useState("");
  const [bookKindss, setBookKindss] = useState("");
  const [isUpdate, setIsUpdate] = useState(false); // Track if it's an update
  const [reviewId, setReviewId] = useState(null); // State to track the review ID for editing
  const [loading, setLoading] = useState(true); // Track loading state
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
  const [bookI,setBookI]=useState(null);
  const [ean, setEan] = useState(null);

  const userId = auth?.user?._id;
  // const [rating, setRating] = useState(5);

  const handleBookKindChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      // Add the selected value to the array
      setBookKindss((prev) => [...prev, value]);
    } else {
      // Remove the value from the array if unchecked
      setBookKindss((prev) => prev.filter((kind) => kind !== value));
    }
  };
  const handleCancelCan = async (reviewId) => {
    try {
      if (!reviewIdD)
        return toast.error("Review ID is required to delete a review ❌");
      // Call your delete API
      const res = await axios.delete(`api/v1/review/${reviewIdD}`);

      if (res.status === 200) {
        toast.success("Review deleted successfully ✅");
        setShowCan(false);
        setRec((rec) => !rec);
        setReviewIdD(null); // Reset reviewIdD after deletion
        // Optional: refresh reviews list if you have state
      } else {
        toast.error("Failed to delete review ❌");
      }
    } catch (error) {
      console.error("Delete review error:", error);
      toast.error("Something went wrong while deleting the review ❌");
    }
  };
  const handleSubmitReview = async (event, from, rating) => {
    event.preventDefault();

    // Check if the user is logged in (Assuming userId is available only if logged in)
    if (!userId) {
      toast.error("Please log in first to submit a review.");
      return;
    }
    if (
      from == "review" &&
      (!title || !description || bookKindss.length === 0)
    ) {
      toast.error(
        "Please fill in all fields and select at least one reading format."
      );
      return;
    }
    if (!user || !user._id) {
      toast.error("You must be logged in to access this feature");
      setShowLoginM(true);
      return;
    }
    if (hasSubmittedReview) {
      toast.error("You have already submitted a review for this book.");
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const offilioFlag = searchParams.get("offilio");
    const searchBookFlag = searchParams.get("searchbook");
    const bookDetailFlag = searchParams.get("bookdetail"); // ✅ Extract bookdetail

    // Ensure bookId is set - try multiple sources
    let finalBookId = bookId || 
                       isSearchBookSet?.bookId || 
                       isSearchBookSet?.book?._id || 
                       isSearchBookSet?._id;
    
    // Ensure EAN is set - try multiple sources
    const finalEAN = isSearchBookSet?.EAN || 
                     isSearchBookSet?.book?.EAN || 
                     (searchParams.get("ean") || null);

    // Helper function to check if a string is a valid MongoDB ObjectId
    const isValidObjectId = (id) => {
      if (!id || typeof id !== 'string') return false;
      // MongoDB ObjectId is 24 hex characters
      return /^[0-9a-fA-F]{24}$/.test(id);
    };

    // If finalBookId is not a valid ObjectId (e.g., it's an EAN), set it to null
    // We'll only use EAN in that case
    if (finalBookId && !isValidObjectId(finalBookId)) {
      // If bookId looks like an EAN (not a valid ObjectId), don't send it
      // The backend will use EAN instead
      if (finalEAN && finalBookId === finalEAN) {
        finalBookId = null; // Don't send bookId if it's the same as EAN
      } else if (!finalEAN) {
        // If we don't have EAN but bookId is not valid ObjectId, 
        // it might be an EAN - use it as EAN instead
        // This case is handled by the backend now
      }
    }

    if (!finalBookId && !finalEAN) {
      toast.error("Book information is missing. Please try searching again.");
      return;
    }

    try {
      const response = await axios.post("api/v1/review/addreview", {
        bookId: finalBookId || undefined, // Only send if it's a valid ObjectId
        userId: user?._id,
        title,
        description,
        rating: rating || ratingUser,
        bookKindss,
        EAN: finalEAN, // 👈 Add EAN if available
        offilio: offilioFlag,
        searchbook: searchBookFlag,
        bookdetail: bookDetailFlag, // ✅ Add bookdetail flag
      });

      toast.success(response.data.message);
      setTitleBook("");
      setDescription("");
      setRatingUser(0);
      setBookKindss([]);
      // Clear checkboxes manually
      document
        .querySelectorAll("input[name='bookKindss']")
        .forEach((checkbox) => {
          checkbox.checked = false;
        });
      setRec((rec) => !rec);
      fetchUserLastReview();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };
  const handleUpdateReview = async (event, from, rating) => {
    event.preventDefault();

    // Check if the user is logged in (Assuming userId is available only if logged in)
    if (!userId) {
      toast.error("Please log in first to submit a review.");
      return;
    }
    if (
      from == "review" &&
      (!title || !description || bookKindss.length === 0)
    ) {
      toast.error(
        "Please fill in all fields and select at least one reading format."
      );
      return;
    }
    if (!user || !user._id) {
      toast.error("You must be logged in to access this feature");
      setShowLoginM(true);
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const offilioFlag = searchParams.get("offilio");
    const searchBookFlag = searchParams.get("searchbook");
    const bookDetailFlag = searchParams.get("bookdetail"); // ✅ Extract bookdetail

    try {
      const response = await axios.put("api/v1/review/update-review", {
        bookId,
        reviewId, // Include reviewId for updating
        userId: user?._id,
        title,
        description,
        rating: rating || ratingUser,
        bookKindss,
        EAN: storedBook?.EAN, // 👈 Add EAN if available
        offilio: offilioFlag,
        searchbook: searchBookFlag,
        bookdetail: bookDetailFlag, // ✅ Add bookdetail flag
      });

      toast.success("Review Updated Successfully!");
      setTitleBook("");
      setDescription("");
      setRatingUser(0);
      setBookKindss([]);
      // Clear checkboxes manually
      document
        .querySelectorAll("input[name='bookKindss']")
        .forEach((checkbox) => {
          checkbox.checked = false;
        });
      setRec((rec) => !rec);
      fetchUserLastReview();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };
  // Define fetchReviews outside of useEffect so you can call it from other functions
  const fetchReviews = async (bookIdFromURL, offilioFlag, searchBookFlagg) => {
    if (!bookIdFromURL) return; // Wait until bookId is available
    
    const searchParams = new URLSearchParams(location.search);
    const searchBookFlag = searchParams.get("searchbook") === "true";
    const bookDetailFlag = searchParams.get("bookdetail") === "true";
    const offilioFlagL = searchParams.get("offilio");
    const eanFromURL = searchParams.get("ean");

    setLoading(true); // Start loading
    try {
      // Build query params correctly
      let queryParams = new URLSearchParams();
      if (eanFromURL) {
        queryParams.append("offilio", "ean");
      } else if (searchBookFlag) {
        queryParams.append("searchbook", "true");
        queryParams.append("offilio", "false");
      } else if (bookDetailFlag) {
        queryParams.append("bookDetail", "true");
        queryParams.append("offilio", "false");
      } else if (offilioFlagL) {
        queryParams.append("offilio", offilioFlagL);
      }

      const response = await axios.get(
        `api/v1/review/getreviews/${bookIdFromURL}?${queryParams.toString()}`
      );
      
      console.log("📦 Full API Response:", response.data);
      
      const reviewsData = response.data.reviews || [];
      setReviews(reviewsData);

      setAverageRating(response.data.averageRating || 0);
      setTotalReviews(response.data.totalReviews || 0);

      // Extract book info from API response - API data takes precedence
      const { bookCoverImage, author, description, title, bookId } = response.data;

      // Always set book info from API if available (don't use conditional checks)
      if (bookCoverImage) {
        setBookCoverImage(bookCoverImage);
      }
      if (bookId) {
        setBookI(bookId);
      }
      if (author) {
        setAuthor(author);
      }
      if (description) {
        setDescriptions(description);
      }
      if (title) {
        setTitless(title);
      }

      console.log("📚 Book Info Set:", { bookCoverImage, author, title, description, bookId });
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error(error.response?.data?.message || "Failed to fetch reviews");
      setLoading(false);
    }
  };

  const searchParams = new URLSearchParams(location.search);
  const searchbook = searchParams.get("searchbook");
  const bookDetail = searchParams.get("bookdetail");

  // Helper function to capitalize the first letter of a name
  const capitalizeFirstLetter = (name) => {
    if (!name) return ""; // Handle null or undefined names
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const fetchUserLastReview = async () => {
    const searchParams = new URLSearchParams(location.search);
    const eanFromURL = searchParams.get("ean"); // ✅ check EAN
    const bookIdFromURL = searchParams.get("bookid");

    // ✅ Prefer EAN, otherwise fallback to bookId
    const bookIdentifier =
      eanFromURL ||
      isSearchBookSet?.EAN ||
      bookIdFromURL ||
      isSearchBookSet?.book?.bookId ||
      isSearchBookSet?.book?._id;

    if (!bookIdentifier) return; // nothing to query
    if (!userId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `api/v1/review/getuserlastreview/${bookIdentifier}/${userId}`
      );

      const lastReview = response.data.lastReview;
      console.log("Last Review:", lastReview, bookIdentifier);
      if (
        (lastReview && lastReview?.EAN == bookIdentifier) ||
        lastReview?.bookId == bookIdentifier ||
        lastReview?.offilioBookId == bookIdentifier ||
        lastReview?.bookDetailsId == bookIdentifier
      ) {
        if (lastReview && lastReview.rating) {
          setRating(lastReview.rating); // Display the last rating the user gave
          setHasSubmittedReview(true); // User has submitted a review
        } else {
          setHasSubmittedReview(false); // No review found
        }
      }

      setLoading(false); // End loading
    } catch (error) {
      console.error(error);
    
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUserLastReview();
  }, [isSearchBookSet?.EAN, userId]);

  // BUT ON
  // API FTACH STORE
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { data } = await axios.get("api/v1/stores/"); // Update with your actual API URL
        setCountries(data); // Assuming API response structure is { stores: [...] }
      } catch (error) {
        console.error("Error fetching stores:", error);
      }
    };

    fetchStores();
  }, []);

  const [users, setUsers] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return; // Don't make API call if userId is undefined
      try {
        const res = await axios.get(`/api/v1/auth/user-profile/${userId}`);
        setUsers(res.data.data);
      } catch (err) {
        console.error("Error:", err);
      }
    };

    fetchUser();
  }, [userId]);

  // TXT FILE
  const { isbn } = useParams(); // 👈 this defines isbn
  const [descriptionAbout, setDescriptionAbout] = useState("");

  // const aboutBook = descriptions[isbn];

  useEffect(() => {
    if (!isbn) return;

    const fetchDescription = async () => {
      try {
        const res = await fetch(`/bookdescription/${isbn}`);
        const data = await res.json();
        setDescriptionAbout(data.descriptionAbout);
      } catch (err) {
        setDescriptionAbout("Description not found.");
      } finally {
        setLoading(false);
      }
    };

    fetchDescription();
  }, [isbn]);

  // Integration Reviews - Load from localStorage as initial fallback
  useEffect(() => {
    storedBook = JSON.parse(localStorage.getItem("selectedBookforreviewsnew"));
    console.log("Stored Book from localStorage:", storedBook);
    setIsSearchBookSet(storedBook);
    
    // Use localStorage only as initial fallback before API data loads
    // API data will always override this when fetchReviews completes
    if (storedBook) {
      // Set initial values from localStorage (will be overridden by API)
      if (storedBook.author && !author) setAuthor(storedBook.author);
      if (storedBook.titless && !titless) setTitless(storedBook.titless);
      if (storedBook.bookCoverImage && !bookCoverImage) setBookCoverImage(storedBook.bookCoverImage);
      if (storedBook.descriptions && !descriptions) setDescriptions(storedBook.descriptions);
    }
  }, []); // Only run once on mount

  // 👈 isSearchBookSet depend me rakhna zaroori hai

  const shareEvent = (platform) => {
    const eventId = bookId || "defaultId"; // fallback if _id is missing
    const textToShare = `${Frontend}/reviews?bookid=${eventId}`;
    const encodedText = encodeURIComponent(`Check this out: ${textToShare}`);

    let shareUrl = "";
    switch (platform) {
      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodedText}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          textToShare
        )}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          textToShare
        )}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(
          textToShare
        )}&text=${encodedText}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=Check%20this%20event&body=${encodedText}`;
        break;
      case "copy":
        navigator.clipboard.writeText(textToShare).then(() => {
          toast.success("Link copied to clipboard!");
        });
        return;
      default:
        return;
    }

    window.open(shareUrl, "_blank");
  };
  const searchParamss = new URLSearchParams(location.search);

  const bookDetails = searchParamss.get("bookdetail");
  const [choiceOpen, setChoiceOpen] = useState(false);

  const handleShare = (r) => alert(`Share review by ${r.user}`);
  const handleEdit = (r) => alert(`Edit review #${r.id}`);
  const handleDelete = (r) => alert(`Delete review #${r.id}`);

  // Write review local state
  const [myRating, setMyRating] = useState(0);
  const [myTitle, setMyTitle] = useState("");
  const [myText, setMyText] = useState("");
  const [myFormat, setMyFormat] = useState("book"); // "book" | "ebook" | "audiobook"
  const [formError, setFormError] = useState("");
  const submitReview = (e) => {
    e.preventDefault();
    if (!myTitle.trim()) {
      setFormError("Title is required.");
      return;
    }
    setFormError("");
    alert(
      `Submitted review:\nTitle: ${myTitle}\nRating: ${myRating}★\nFormat: ${myFormat}\nText: ${myText.slice(
        0,
        120
      )}...`
    );
    setMyRating(0);
    setMyTitle("");
    setMyText("");
    setMyFormat("book");
  };
 const eventTitle = `${titless} - Reviews` || "Book Reviews on Ofellio";
    
    
    
      const baseApiUrl = `${BASE_URL}/api/share/review`;
      const shareUrl = `${baseApiUrl}/${bookI || ean || bookId}`; // Use review-specific share endpoint
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedTitle = encodeURIComponent(eventTitle || "Book Reviews on Ofellio");
 
  return (
    <>
      <div className="clintheriv position-fixed z-2 w-100 left-0 top-0">
        <Header3 />
      </div>
      {alertVisible && (
        <div
          style={{
            position: "absolute", // Overlay on top
            top: "14%",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "orange",
            color: "white",
            padding: "10px 10px",
            borderRadius: "5px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // width: "400px",
            // boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
          }}
        >
          {/* Tick Icon */}
          <div
            style={{
              backgroundColor: "white",
              color: "green",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "10px",
              fontWeight: "bold",
              fontSize: "16px",
              border: "1px solid green",
            }}
          >
            ✓
          </div>
          <div className="fontmobil" style={{ flex: 1 }}>
            {lg?.reviewPublished || "Review published"}
          </div>
          {/* Close Icon */}
          <div
            style={{
              cursor: "pointer",
              marginLeft: "10px",
              color: "white",
              fontWeight: "bold",
              fontSize: "18px",
            }}
            onClick={handleCloseAlert}
          >
            ×
          </div>
        </div>
      )}
      <div className="container d-none conreicx mtopfixed scalmin1 mbscalmin">
        {/* Book Details and Sidebar */}
        <div className="row ">
          <div className="col-xl-3 col-12   px-0 ">
            <div
              className={`positionfix start-0 mx-5 bookposicenterlg mawxnone  `}
              style={{
                position: "sticky",
                top: `${topOffset}%`,
                maxHeight: "24vh",
                height: "100%",
                transition: "top 0.3s ease-out",
              }}
            >
              <div
                style={{
                  minHeight: "calc(100vh - 466px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                }}
              // className="custom-scroll"
              >
                {/* Book Image and Buttons */}
                <div>
                  {sanitizedCoverImage && (
                    <img
                      src={sanitizedCoverImage}
                      alt="Book Cover"
                      className="img-fluid  imagwidcl mb-2 d-flex mx-auto  bookimgrev bookposifix text-center"
                      width="307"
                      height="387"
                    />
                  )}
                </div>

                <div className="d-flex justify-content-center">
                  <button
                    className="btn btn-primary mt-1 border-0 fw600 fs-5  ftzevtt posisthieh  posistsiz wmtread text-center text-white  w-100 mb-2 rounded-1"
                    style={{
                      backgroundColor: "#62C0C7",
                      maxWidth: "165px",
                      height: "44px",
                    }}
                  >
                    <div
                      className="d-flex justify-content-center align-items-center gap-2"
                      onClick={handleShowW}
                    >
                      {buttonText} {/* Dynamically Update Text */}
                      {/* Want to read */}
                      <img
                        src={require("./image/reviewvtn.png")}
                        width="11"
                        height="7"
                      />
                    </div>
                  </button>
                </div>
                <div className="d-flex justify-content-center">
                  <button
                    className={`btn btn-primary border-0 fw600 wmtread ftzevtt ${lg === "english" || lg === "nihongo"
                      ? "posisthieh"
                      : "lghight"
                      } posistsiz fs-5   text-center text-white  w-100  rounded-1`}
                    style={{
                      backgroundColor: "#62C0C7",
                      maxWidth: "165px",
                      height: "44px",
                    }}
                    onClick={handleScrollToForm}
                  >
                    {/* Write a review */}
                    {lg?.rewbtn2}
                  </button>
                </div>
                <div className="d-flex justify-content-center ">
                  <button
                    className={`btn btn-primary ${lg === "english" || lg === "nihongo"
                      ? "posisthieh"
                      : "lghight"
                      } border-0 fw600 wmtread posistsiz ftzevtt fs-5 mt-2  text-center text-white  w-100 mb-2 rounded-1`}
                    style={{
                      backgroundColor: "#62C0C7",
                      maxWidth: "165px",
                      height: "44px",
                    }}
                    onClick={() => {
                      navigate(
                        `/createmeetup?title=${encodeURIComponent(titless)}&author=${encodeURIComponent(author)}`
                      );
                    }}
                  >
                    {/* Use for Book Duo */}
                    {lg?.rewbtn3}
                  </button>
                </div>

                <div className="d-flex mtminnoneif justify-content-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      onClick={(event) => {
                        if (!hasSubmittedReview) {
                          handleRating(event, star);
                        }
                      }}
                      // onClick={() => handleRatingUser(star)}
                      style={{
                        cursor: hasSubmittedReview ? "default" : "pointer",
                        fontSize: "25px",
                        color: star <= rating ? "#FFD700" : "#ccc", // Gold color for filled stars
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>

                <div
                  className="text-dark fw600 text-center"
                  style={{ fontSize: "12px" }}
                >
                  {/* Rate this book */}
                  {lg?.rateBook}
                </div>

                <div
                  className="w-100 mt-2 mx-auto mx-xl-0"
                  style={{ maxWidth: "327px", border: "1px solid #B8B8B8" }}
                ></div>

                {/* Placeholder for store icons */}

                <div className="dropdown ">
                  <div
                    className="d-xl-block w-100 justify-content-center mx-auto d-none px-2 py-1 dropdown-toggle"
                    style={{
                      border: "1px solid #7F7F7F",
                      maxWidth: "327px",
                      borderRadius: "8px",
                      marginTop: "28px",
                      cursor: "pointer",
                    }}
                    id="buyOnDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fs-5 text-dark fw-bold">
                        {/* Buy on: */}
                        {lg?.buyon}:
                      </div>
                      <div>
                        <img
                          src={require("./image/arrowback.png")}
                          width="16px"
                          height="16px"
                          alt="Arrow"
                        />
                      </div>
                    </div>
                  </div>

                  <ul
                    className="dropdown-menu dropdoenhight py-0 "
                    aria-labelledby="buyOnDropdown"
                    style={{ border: "1.5px solid black" }}
                  >
                    {countries?.length > 0 ? (
                      countries.map((country, index) => (
                        <li key={index} className="dropdown-submenu">
                          <div
                            className="d-flex justify-content-between align-items-center hoverclass "
                            style={{ cursor: "pointer" }}
                          >
                            <a
                              className="dropdown-item dropdown-toggle hoverclass "
                              style={{ paddingLeft: "5px", paddingRight: "0" }}
                            >
                              {country?.country}
                            </a>
                            <div>
                              <img
                                src={require("./image/arrowback.png")}
                                width="11px"
                                height="11px"
                                style={{
                                  rotate: "-90deg",
                                  marginRight: "10px",
                                }}
                                alt="Arrow"
                              />
                            </div>
                          </div>

                          {index !== countries.length - 1 && (
                            <div
                              className="w-100"
                              style={{ border: "1px solid black" }}
                            ></div>
                          )}

                          <ul
                            className="dropdown-menu dropdoenhight py-0"
                            style={{ border: "1.5px solid black" }}
                          >
                            {country?.stores?.length > 0 ? (
                              country.stores.map((store, storeIndex) => (
                                <li key={storeIndex}>
                                  <a
                                    className="dropdown-item hoverclass"
                                    href={store?.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {store?.name}
                                  </a>
                                  {storeIndex !== country.stores.length - 1 && (
                                    <div
                                      className="w-100"
                                      style={{ border: "1px solid black" }}
                                    ></div>
                                  )}
                                </li>
                              ))
                            ) : (
                              <li className="dropdown-item text-center">
                                No stores available
                              </li>
                            )}
                          </ul>
                        </li>
                      ))
                    ) : (
                      <li className="dropdown-item text-center">
                        No stores available
                      </li>
                    )}
                  </ul>
                </div>

                <div className="dropdown ">
                  <div
                    className="d-block w-100 justify-content-center mx-auto d-xl-none px-2 py-1 dropdown-toggle"
                    style={{
                      border: "1px solid #7F7F7F",
                      maxWidth: "327px",
                      borderRadius: "8px",
                      marginTop: "28px",
                      cursor: "pointer",
                    }}
                    id="buyOnDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fs-5 mobil text-dark fw-bold">
                        {/* Buy on: */}
                        {lg?.buyon}:
                      </div>
                      <div>
                        <img
                          src={require("./image/arrowback.png")}
                          width="16px"
                          height="16px"
                          alt="Arrow"
                          className="byarrow"
                        />
                      </div>
                    </div>
                  </div>

                  <ul
                    className="dropdown-menu dropdoenhight py-0 "
                    aria-labelledby="buyOnDropdown"
                    style={{ border: "1.5px solid black" }}
                  >
                    {countries?.length > 0 ? (
                      countries.map((country, index) => (
                        <li key={index} className="dropdown-submenu">
                          <div
                            className="d-flex justify-content-between align-items-center hoverclass "
                            style={{ cursor: "pointer" }}
                          >
                            <a
                              className="dropdown-item dropdown-toggle hoverclass "
                              style={{ paddingLeft: "5px", paddingRight: "0" }}
                            >
                              {country?.country}
                            </a>
                            <div>
                              <img
                                src={require("./image/arrowback.png")}
                                width="11px"
                                height="11px"
                                style={{
                                  rotate: "-90deg",
                                  marginRight: "10px",
                                }}
                                alt="Arrow"
                              />
                            </div>
                          </div>

                          {index !== countries.length - 1 && (
                            <div
                              className="w-100"
                              style={{ border: "1px solid black" }}
                            ></div>
                          )}

                          <ul
                            className="dropdown-menu dropdoenhight py-0"
                            style={{ border: "1.5px solid black" }}
                          >
                            {country?.stores?.length > 0 ? (
                              country.stores.map((store, storeIndex) => (
                                <li key={storeIndex}>
                                  <a
                                    className="dropdown-item hoverclass"
                                    href={store?.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {store?.name}
                                  </a>
                                  {storeIndex !== country.stores.length - 1 && (
                                    <div
                                      className="w-100"
                                      style={{ border: "1px solid black" }}
                                    ></div>
                                  )}
                                </li>
                              ))
                            ) : (
                              <li className="dropdown-item text-center">
                                No stores available
                              </li>
                            )}
                          </ul>
                        </li>
                      ))
                    ) : (
                      <li className="dropdown-item text-center">
                        No stores available
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-lg-9  ">
            <div className=" playposi mt-3">
              {/* Book Title and Author */}

              <div
                className="fw-bold mt-lg-0 mt-5 playfont  "
                style={{ fontSize: "32px ", lineHeight: "40px" }}
              >
                {/* Playground */}
                {titless}
              </div>

              <div
                className="fw-normal mt-2 ftzevtn"
                style={{ fontSize: "24px", lineHeight: "40px" }}
              >
                {/* Richard Powers */}
                {author}
              </div>

              <div>
                {loading && <div>Loading...</div>}
                {!loading && (
                  <div className="mt-3">
                    <span className="text-dark fw600 fs-18">
                      {averageRating}
                    </span>
                    <span
                      className="mx-2 text-warning"
                      style={{ fontSize: "24px" }}
                    >
                      {"★".repeat(Math.floor(averageRating))}
                      {averageRating % 1 >= 0.5 && "★"}
                      <span style={{ color: "#D9D9D9" }}>
                        {"☆".repeat(5 - Math.ceil(averageRating))}
                      </span>
                    </span>
                    <span className="text-dark fw600 fs-18">
                      {totalReviews}{" "}
                    </span>
                  </div>
                )}
              </div>

              <div
                className="w-100 border my-3"
                style={{ border: "#B8B8B8", maxWidth: "915px" }}
              ></div>

              {/* Author Information */}
              <div className="mt-4">
                {bookDetails == "true" || bookDetails == true ? null : (
                  <>
                    <div
                      className="fw-bold text-dark  aboutauther "
                      style={{ fontSize: "22px" }}
                    >
                      About the book
                    </div>

                    <div
                      className="fw-normal fs-6 w-100 mt-3 ftzevtt"
                      style={{ maxWidth: "912px", whiteSpace: "pre-wrap" }}
                    >
                      {descriptions}
                    </div>

                    <div
                      className="w-100 border my-4"
                      style={{ border: "#B8B8B8", maxWidth: "915px" }}
                    ></div>
                  </>
                )}
              </div>

              {/* Reviews */}
              <div className="mt-4">
                <div
                  className="fw-bold aboutauther "
                  style={{ fontSize: "22px" }}
                >
                  Reviews
                </div>

                <div className="mt-4">
                  {displayedReviews.length > 0 ? (
                    displayedReviews.map((review) => (
                      <div key={review._id} className="mb-4">
                        <div
                          className="d-flex gap-1 align-items-center"
                          onClick={() => navigate("/leading")}
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            src={
                              review.userId?.profileImage
                                ? `${BASE_URL}/uploads/${review.userId.profileImage}`
                                : require("../pages/image/addphotoicon.png")
                            }
                            alt={`${review.userId?.name || "User"} photo`}
                            className="rounded-circle me-2 pualimgrew reviimg"
                            width="60"
                            height="60"
                            style={{ width: "60px", height: "60px" }}
                          />
                          <div
                            style={{ fontSize: "18px" }}
                            className="fw600 fs-22 ftlink d-flex align-items-center gap-2 w-100"
                          >
                            <div>
                              {capitalizeFirstLetter(review.userId?.name) ||
                                "Unknown User"}{" "}
                              on {formatDate(review.createdAt)}
                            </div>

                            {review.userId?._id === userId && (
                              <div
                                className="dropdown"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  id={`dropdownMenuButton-${review._id}`}
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  style={{
                                    fontSize: "25px",
                                    fontWeight: "bolder",
                                  }}
                                >
                                  ⋮
                                </button>
                                <ul
                                  className="dropdown-menu"
                                  aria-labelledby={`dropdownMenuButton-${review._id}`}
                                >
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => {
                                        setSShow(true);
                                      }}
                                    >
                                      Share
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => {
                                        setIsUpdate(true);
                                        setRatingUser(review.rating);
                                        setDescription(review.description);
                                        setTitleBook(review.title);
                                        setBookKindss(review.bookKindss);
                                        setReviewId(review._id);
                                        handleScrollToForm();
                                      }}
                                    >
                                      Edit
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      onClick={() => {
                                        setReviewIdD(review._id);
                                        handleShowCan();
                                      }}
                                      className="dropdown-item text-danger"
                                    >
                                      Delete
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className="text-warning starratin"
                          style={{ fontSize: "24px" }}
                        >
                          {"★".repeat(review.rating)}
                          <span style={{ color: "#D9D9D9" }}>
                            {"☆".repeat(5 - review.rating)}
                            <span
                              className="text-dark mx-3 fw-bold ftlink"
                              style={{ fontSize: "19px" }}
                            >
                              {review.title}
                            </span>
                          </span>
                        </div>
                        <div
                          className="fw-normal fs-6 w-100 mt-2 ftzevtt"
                          style={{ maxWidth: "912px" }}
                        >
                          {review.description}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted mt-4">
                      {lg?.noReviewsAvailable || "No Reviews Available"}
                    </div>
                  )}
                </div>
                {!showAll && reviews.length > 3 && (
                  <div
                    className="fw600 fs-18 ftzevtt"
                    style={{ color: "#34C3C9", cursor: "pointer" }}
                    onClick={handleSeeMoreClick}
                  >
                    See more reviews
                  </div>
                )}

                <div
                  className="w-100 border my-4"
                  style={{ border: "#B8B8B8", maxWidth: "915px" }}
                ></div>
              </div>

              {/* Review Form */}
              <div className="mt-4">
                <div
                  className=" mb-3 fw-bold aboutauther "
                  style={{ fontSize: "22px" }}
                >
                  {/* Review */}
                  {lg?.reviewText}
                </div>

                <div
                  className="w-100 rounded-5 p-4"
                  style={{
                    backgroundColor: "#F2F2F2",
                    maxWidth: "915px",
                    height: "461px",
                  }}
                >
                  {/* Review Form Section */}
                  <div className="mb-5">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (isUpdate) {
                          handleUpdateReview(e, "review");
                        } else {
                          handleSubmitReview(e, "review");
                        }
                      }}
                    >
                      <div className="d-flex gap-5">
                        <div className="mb-2">
                          <div className="fw-bold fs-18 ftlink ">
                            {/* Your rating */}
                            {lg?.ratingtext}
                          </div>
                          <div className="d-flex justify-content-center">
                            {[1, 2, 3, 4, 5].map((stars) => (
                              <span
                                className="starratin"
                                key={stars}
                                onClick={() => handleRatingUser(stars)}
                                style={{
                                  cursor: "pointer",
                                  fontSize: "26px",
                                  color:
                                    stars <= ratingUser ? "#FFD700" : "#ccc", // Gold color for filled stars
                                }}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* Reading Format Section */}
                        <div className="mb-3">
                          <div className="fw-bold fs-18 ftlink">
                            {/* Read on */}
                            {lg?.readon}
                          </div>
                          <div className="form-check  form-check-inline mt-2 ">
                            <input
                              className="form-check-input  widhe rounded-0  border-dark placeholder-login"
                              type="checkbox"
                              id="book"
                              value="book"
                              name="bookKindss"
                              checked={bookKindss.includes("book")}
                              onChange={(e) => handleBookKindChange(e)}
                            />
                            <label
                              className="form-check-label mx-1 fw600"
                              htmlFor="book"
                            >
                              {/* Book */}
                              {lg?.book}
                            </label>
                          </div>
                          <div className="form-check mx-md-3 form-check-inline">
                            <input
                              className="form-check-input widhe rounded-0  border-dark placeholder-login"
                              type="checkbox"
                              id="ebook"
                              value="ebook"
                              checked={bookKindss.includes("ebook")}
                              name="bookKindss"
                              onChange={(e) => handleBookKindChange(e)}
                            />
                            <label
                              className="form-check-label fw600 mx-1"
                              htmlFor="ebook"
                            >
                              {/* E-book */}
                              {lg?.ebook}
                            </label>
                          </div>
                          <div className="form-check  mx-md-3 form-check-inline">
                            <input
                              className="form-check-input widhe rounded-0  border-dark  placeholder-login"
                              type="checkbox"
                              id="audiobook"
                              value="audiobook"
                              name="bookKindss"
                              checked={bookKindss.includes("audiobook")}
                              onChange={(e) => handleBookKindChange(e)}
                            />
                            <label
                              className="form-check-label fw600 mx-1"
                              htmlFor="audiobook"
                            >
                              {/* Audiobook */}
                              {lg?.audiobook}
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fs-18 fw-bold ftlink">
                          {/* Review's title */}
                          {lg?.revTitle}
                        </label>
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => setTitleBook(e.target.value)}
                          className="form-control  border-dark rounded-0 w-100 placeholder-login"
                          style={{ maxWidth: "832px", height: "38px" }}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label fs-18 fw-bold ftlink">
                          {/* Review */}
                          {lg?.reviewtext}
                        </label>
                        <textarea
                          className="form-control   border-dark rounded-0 w-100 placeholder-login"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows="3"
                          style={{ maxWidth: "832px", height: "137px" }}
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-success text-white w-100 py-2 subbtnrev  ftzevtt fw600 fs-18 border-0 rounded-1 mt-2"
                        style={{
                          backgroundColor: "#62C0C7",
                          maxWidth: "143px",
                          height: "auto",
                        }}
                      // onClick={handleSubmitRe}
                      >
                        {/* Submit */}
                        {isUpdate ? (lg?.update || "Update") : (lg?.submitbtn || "Submit")}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={wShow}
        onHide={handleCloseW}
        centered
        className="custom-gropshare  custom-gropwent"
        backdrop={true}
      >
        <Modal.Header className=" border-0">
          <img
            className=" border-0 mt-5 mx-2 top-0 closebtnevtn closebtnevtn3  closebtnevtn3cl "
            src={require("../components/image/close.png")}
            alt="logo"
            onClick={handleCloseW}
          />
        </Modal.Header>
        <Modal.Title className="mx-auto  ">
          <div className=" fw-bold fs-4 text-center mt-4 aboutauther  ">
            {lg?.makeAChoiceForThisBook || "Make a choice for this book"}
          </div>
        </Modal.Title>
        <Modal.Body className="mx-4 ">
          <div className="d-flex mt-3 justify-content-center">
            <button
              className="btn btn-primary border-0 fw600 wmtread ftzevtt fs-5 mt-2  text-center text-white  w-100 mb-2 rounded-2"
              style={{
                backgroundColor: "#62C0C7",
                maxWidth: "190px",
                height: "44px",
              }}
              // onClick={() => setGlobalOption("Want to Read")}
              onClick={() => handleSelection(lg?.rewbtn1)}
            >
              {/* Want to read */}
              {lg?.rewbtn1}
            </button>
          </div>

          <div className="d-flex justify-content-center">
            <button
              className="btn btn-primary border-0 fw600 wmtread ftzevtt fs-5 mt-2  text-center text-white  w-100 mb-2 rounded-2"
              style={{
                backgroundColor: "#62C0C7",
                maxWidth: "190px",
                height: "44px",
              }}
              // onClick={() => setGlobalOptionRead("Read")}
              onClick={() => handleSelection(lg?.Read)}
            >
              {/* Read */}
              {lg?.Read}
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        //  show={sShow}
        show={false}
        onHide={handleCloseS}
        centered
        className="custom-gropshare"
        backdrop={true}
      >
        <Modal.Header className=" border-0">
          <img
            className=" border-0 closebtnevtn closebtnevtn3"
            src={require("../components/image/close.png")}
            alt="logo"
            onClick={handleCloseS}
          />
        </Modal.Header>
        <Modal.Title className="mx-auto  ">
          <div
            className=" fw-bold text-center  aboutauther  "
            style={{ fontSize: "27px" }}
          >
            {lg?.shareThisEvent || "Share this event"}
          </div>
        </Modal.Title>
        <Modal.Body className="mx-4 ">
          <div className="d-flex gapimgmodel mt-sm-2 mx-sm-5 flex-wrap justify-content-center">
            <img
              src={require("./image/whatsappmodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}

              onClick={() => shareEvent("whatsapp")}
            />
            <img
              src={require("./image/fbmodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("facebook")}
            />
            <img
              src={require("./image/telegrmmodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("telegram")}
            />
            <img
              src={require("./image/instamodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("instagram")}
            />
            <img
              src={require("./image/tiktkomodel.png")}
              width="35"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("tiktok")}
            />
            <img
              src={require("./image/twetrmodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("twitter")}
            />
            <img
              src={require("./image/linkedmodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("linkedin")}
            />
            <img
              src={require("./image/modeemail.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("email")}
            />
            <img
              src={require("./image/copymodel.png")}
              width="40"
              height="40"
              // onClick={handleShowSL}
              onClick={() => shareEvent("copy")} // ✅ Corrected onClick handler
            />
          </div>
        </Modal.Body>
      </Modal>
      <div className="min-h-screen bg-gradient-to-b from-white to-[#f7f7f7] pb-20 pt-[140px]">
        <Container>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {/* Left sticky column */}
            <div className="md:col-span-3">
              <div className="md:sticky md:top-6 md:space-y-5 ">
                <Card className="">
                  <a className="group block mx-auto h-[225px] w-[150px] overflow-hidden rounded-md ring-1 ring-[#e9e9e9]">
                    {sanitizedCoverImage && (
                      <img
                        src={sanitizedCoverImage}
                        alt="Book cover"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    )}
                  </a>
                  <div className="sm:hidden mt-[8px]">
                    <h1 className="text-2xl font-bold tracking-tight">
                      {titless}
                    </h1>
                    <p className="mt-1 text-slate-600">{author}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <Stars rating={averageRating} disabled={true} />
                      <span className="text-sm font-medium text-slate-700">
                        {Number(averageRating)?.toFixed(1)}
                      </span>
                      <span className="text-sm text-slate-500">
                        ({totalReviews})
                      </span>
                    </div>
                    {bookDetails == "true" || bookDetails == true ? null : (
                      <div className="sm:hidden mt-[8px]">
                        {" "}
                        <h2 className="text-lg font-semibold ">
                          {lg?.aboutTheBook || "About the book"}
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-slate-800">
                          {descriptions}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <Button
                      onClick={() => {
                        if (!user || !user._id) {
                          toast.error("You must be logged in to access this feature");
                          setShowLoginM(true);
                          return;
                        }
                        setChoiceOpen(true)
                      }}
                      className="w-full"
                    >
                      {lg?.rewbtn1 || "Want to read"}
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={(e) => {
                        if (!user || !user._id) {
                          toast.error("You must be logged in to access this feature");
                          setShowLoginM(true);
                          return;
                        }
                        handleScrollToForm(e)
                      }}
                    >
                      {lg?.writeAReview || "Write a review"}
                    </Button>
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                         if (!user || !user._id) {
                          toast.error("You must be logged in to access this feature");
                          setShowLoginM(true);
                          return;
                        }
                        navigate(
                          `/createmeetup?title=${encodeURIComponent(titless)}&author=${encodeURIComponent(author)}`
                        );
                      }}
                    >
                      {lg?.useForBookDuo || "Use for Book Duo"}
                    </Button>
                      
                    <Share2 className="mx-auto cursor-pointer" onClick={() => {
                      if (bookI) {
                        setSShow(true);
                      }
                      }} color="#34c3c9" />
                   
                  </div>
                  {/* quick star rating */}
                  <div className="mt-4 border-t border-[#f1f1f1] pt-4">
                    <div className="text-center text-xs font-medium text-slate-500">
                      {lg?.rateBook || "Rate this book"}
                    </div>
                    <div className="mt-2 flex items-center justify-center gap-1">
                      <Stars
                        rating={rating}
                        onRate={(star, e) => {
                          if (!hasSubmittedReview) {
                            handleRating(e, star); // pass event + star
                          }
                        }}
                        disabled={hasSubmittedReview}
                      />
                    </div>
                  </div>
                  <div className="mt-5">
                    <BuyOn catalog={buyCatalog} />
                  </div>
                </Card>
              </div>
            </div>

            {/* Right column (scrolls) */}
            <div className="space-y-6 md:col-span-9">
              {/* Header with avg rating */}
              <Card className="hidden sm:block">
                <h1 className="text-2xl font-bold tracking-tight">{titless}</h1>
                <p className="mt-1 text-slate-600">{author}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Stars rating={averageRating} disabled={true} />
                  <span className="text-sm font-medium text-slate-700">
                    {Number(averageRating)?.toFixed(1)}
                  </span>
                  <span className="text-sm text-slate-500">
                    ({totalReviews})
                  </span>
                </div>
              </Card>

              {/* About the book */}
              <Card className="hidden sm:block">
                {bookDetails == "true" || bookDetails == true ? null : (
                  <>
                    {" "}
                    <h2 className="text-lg font-semibold">{lg?.aboutTheBook || "About the book"}</h2>
                    <p className="mt-3 text-sm leading-6 text-slate-800">
                      {descriptions}
                    </p>
                  </>
                )}
              </Card>

              {/* Recent reviews */}
              <Card>
                <h2 className="text-lg font-semibold">{lg?.recentReviews || "Recent reviews"}</h2>
                <div className="divide-y divide-[#f1f1f1]">
                  {displayedReviews.length > 0 ? (
                    displayedReviews.map((review) => (
                      <ReviewRow
                        key={review.id}
                        review={review}
                        onShare={() => {
                        if (bookI) {
                        setSShow(true);
                      }
                        }}
                        onEdit={() => {
                          setIsUpdate(true);
                          setRatingUser(review.rating);
                          setDescription(review.description);
                          setTitleBook(review.title);
                          setBookKindss(review.bookKindss);
                          setReviewId(review._id);
                          handleScrollToForm();
                        }}
                        onDelete={() => {
                          setReviewIdD(review._id);
                          handleShowCan();
                        }}
                        show={review.userId?._id === userId}
                      />
                    ))
                  ) : (
                    <div className="text-center text-muted mt-4">
                      {lg?.noReviewsAvailable || "No Reviews Available"}
                    </div>
                  )}
                </div>
              </Card>

              {/* Write a review (below existing reviews) */}
              <Card>
                <h2 ref={reviewFormRef} className="text-lg font-semibold">
                  {lg?.writeAReview || "Write a review"}
                </h2>
                <form
                  className="mt-4 space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isUpdate) {
                      handleUpdateReview(e, "review");
                    } else {
                      handleSubmitReview(e, "review");
                    }
                  }}
                >
                  {/* Rating */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      {lg?.yourRating || "Your rating"}
                    </label>
                    <div className="mt-2 flex items-center gap-2">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => handleRatingUser(idx + 1)}
                          aria-label={`${idx + 1} stars`}
                          className="text-yellow-400 hover:scale-110 transition-transform"
                        >
                          <Star
                            size={24}
                            className={
                              idx + 1 <= ratingUser
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300"
                            }
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-slate-600">
                        {ratingUser ? `${ratingUser}/5` : (lg?.noRating || "No rating")}
                      </span>
                    </div>
                  </div>

                  {/* Format */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      {lg?.howDidYouReadIt || "How did you read it?"}
                    </label>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="format"
                          value="book"
                          checked={bookKindss.includes("book")}
                          onChange={(e) => handleBookKindChange(e)}
                        />
                        <span>{lg?.book || "Book"}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="format"
                          value="ebook"
                          checked={bookKindss.includes("ebook")}
                          onChange={(e) => handleBookKindChange(e)}
                        />
                        <span>{lg?.ebook || "eBook"}</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="format"
                          value="audiobook"
                          checked={bookKindss.includes("audiobook")}
                          onChange={(e) => handleBookKindChange(e)}
                        />
                        <span> {lg?.audiobook || "Audiobook"}</span>
                      </label>
                    </div>
                  </div>

                  {/* Title (required) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      {lg?.title || "Title"} <span className="text-rose-600">*</span>
                    </label>
                    <input
                      value={title}
                      onChange={(e) => setTitleBook(e.target.value)}
                      required
                      className="mt-1 w-full rounded-xl border border-[#e6e6e6] bg-white px-3 py-2 text-sm focus:border-[#34c3c9] focus:outline-none"
                      placeholder={lg?.addAShortTitle || "Add a short title"}
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      {lg?.yourReview || "Your review"}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-[#e6e6e6] bg-white p-3 text-sm leading-6 focus:border-[#34c3c9] focus:outline-none"
                      rows={6}
                      placeholder={lg?.shareYourThoughts || "Share your thoughts about this book..."}
                    />
                  </div>

                  {/* Error message */}
                  {formError && (
                    <div className="text-sm font-medium text-rose-600">
                      {formError}
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <Button type="submit">{isUpdate ? (lg?.update || "Update") : (lg?.submitbtn || "Submit")}</Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        </Container>

        {/* Want to read modal */}
        <ChoiceModal
          open={choiceOpen}
          active={selectedAction}
          onClose={() => setChoiceOpen(false)}
          onPick={(value) => {
            setChoiceOpen(false);
            setSelectedAction(value);
            if (value === "want") {
              handleSelection(lg?.rewbtn1);
              // handleSelection(lg?.rewbtn1) or your real logic
            } else if (value === "read") {
              handleSelection(lg?.Read);
              // handleSelection(lg?.Read) or your real logic
            }
          }}
        />
      </div>{" "}
      <div className=" " ref={footerRef}>
        {" "}
        <Footer2 />
        <Modal
          show={showCan}
          onHide={handleCloseCan}
          centered
          className="custom-gropmodel heigh"
          backdrop={true}
        >
          <Modal.Header className=" border-0">
            <img
              className=" border-0 closebtnevtn "
              src={require("../components/image/close.png")}
              alt="logo"
              onClick={handleCloseCan}
            />
          </Modal.Header>
          <Modal.Title className="mx-5 ">
            <div
              className=" fw-bold  mt-3 mt-sm-0 mx-md-4 text-center px-md-2 aboutauther  "
              style={{ fontSize: "27px" }}
            >
              {/* Complete your registration */}
              Are you sure you want to delete your review?
            </div>
          </Modal.Title>
          <Modal.Body className="mx-auto mt-3">
            <div className=" text-center ">
              <button
                className="w-100 modelbtnjoin text-white ftzcopy text-center border-0 rounded-1 fw600 "
                style={{
                  maxWidth: "162px ",
                  fontSize: "22px",
                  height: "45px",
                  padding: "0px 20px",
                  backgroundColor: "#34C3C9",
                }}
                onClick={handleCancelCan}
              >
                Delete
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
      {/* Share modal */}
      {sShow && (
           <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setSShow(false)}
                    />
                    <div className="relative z-10 w-full max-w-lg rounded-2xl border-2 border-[#34c3c9] bg-white p-6 shadow-xl mx-4">
                      <div className="mb-3 flex items-start justify-between">
                        <h3 className="text-xl font-semibold text-[#0f172a]">
                          {lg?.shareThisEvent || "Share this event"}
                        </h3>
                        <button
                          className="text-slate-500"
                          onClick={() => setSShow(false)}
                          aria-label="Close share"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {/* WhatsApp */}
                        <a
                          href={`https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>

                        {/* Facebook */}
                        <a
                          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Facebook className="h-4 w-4" /> Facebook
                        </a>

                        {/* Telegram */}
                        <a
                          href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Send className="h-4 w-4" /> Telegram
                        </a>
        
                        {/* Instagram (no API, fallback to link) */}
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Instagram className="h-4 w-4" /> Instagram
                        </a>
        
                        {/* TikTok */}
                        <a
                          href={`https://www.tiktok.com/share?url=${encodedUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Music className="h-4 w-4" /> TikTok
                        </a>
        
                        {/* Twitter/X */}
                        <a
                          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Twitter className="h-4 w-4" /> X/Twitter
                        </a>
        
                        {/* LinkedIn */}
                        <a
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Linkedin className="h-4 w-4" /> LinkedIn
                        </a>
        
                        {/* Snapchat */}
                        <a
                          href={`https://www.snapchat.com/scan?attachmentUrl=${encodedUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Camera className="h-4 w-4" /> Snapchat
                        </a>
        
                        {/* Copy link */}
                        <button
                          onClick={() =>
                            navigator.clipboard.writeText(shareUrl).then(() => {
                              toast.success("Link copied to clipboard!");
                            })
                          }
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <LinkIcon className="h-4 w-4" /> Copy link
                        </button>
        
                        {/* Email */}
                        <a
                          href={`mailto:?subject=${encodedTitle}&body=${encodedUrl}`}
                          className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-[#f9f9f9]"
                        >
                          <Mail className="h-4 w-4" /> Email
                        </a>
                      </div>
                    </div>
                  </div>
      )}
    </>
  );
};

export default Reviews;
